import { useCallback } from 'react';
import * as Sentry from '@sentry/react';

/**
 * Custom hook for consistent error handling with Sentry logging
 * @param {string} componentName - Name of the component using this hook
 * @param {Object} defaultExtras - Default extra data to include with all errors
 * @returns {Function} - Error handler function
 */
export const useErrorHandler = (componentName, defaultExtras = {}) => {
  const handleError = useCallback(
    (error, action, extras = {}, fallbackValue = null) => {
      // Log to Sentry with consistent structure
      Sentry.captureException(error, {
        tags: {
          component: componentName,
          action,
        },
        extra: {
          ...defaultExtras,
          ...extras,
        },
      });

      // Also log to console in development
      if (import.meta.env.DEV) {
        // eslint-disable-next-line no-console
        console.error(`[${componentName}] Error in ${action}:`, error);
      }

      return fallbackValue;
    },
    [componentName, defaultExtras],
  );

  return handleError;
};

/**
 * Higher-order function that wraps a function with error handling
 * @param {Function} fn - Function to wrap
 * @param {Function} errorHandler - Error handler from useErrorHandler
 * @param {string} action - Action name for error logging
 * @param {*} fallbackValue - Value to return on error
 * @returns {Function} - Wrapped function
 */
export const withErrorHandling = (
  fn,
  errorHandler,
  action,
  fallbackValue = null,
) => {
  return (...args) => {
    try {
      return fn(...args);
    } catch (error) {
      return errorHandler(error, action, {}, fallbackValue);
    }
  };
};

/**
 * Hook that provides a safe executor function for operations that might throw
 * @param {string} componentName - Name of the component
 * @param {Object} defaultExtras - Default extras for error logging
 * @returns {Function} - Safe executor function
 */
export const useSafeExecutor = (componentName, defaultExtras = {}) => {
  const handleError = useErrorHandler(componentName, defaultExtras);

  const safeExecute = useCallback(
    (operation, action, fallbackValue = null, extras = {}) => {
      try {
        return operation();
      } catch (error) {
        return handleError(error, action, extras, fallbackValue);
      }
    },
    [handleError],
  );

  return safeExecute;
};
